import React, { useCallback, useEffect, useState } from 'react';

import styled from 'styled-components';

// Debug feature provider removed - using simplified debug logging instead
import { Button, ButtonVariants } from '../Button';
import { AutoPanelLayout } from './AutoPanelLayout';
import { EmbeddedViewer } from './components/EmbeddedViewer';
// Layout wrappers now handled automatically by AutoPanelProvider
import { useAutoLayout } from './hooks/useAutoLayout';
import { LayoutArea } from './types';
import { LayoutPattern } from './utils/layoutStrategies';

const HeaderContent1 = () => (
  <div>
    <h1>Header Area - Navigation</h1>
    <p>This is the main navigation header with branding</p>
  </div>
);

const HeaderContent2 = () => (
  <div>
    <h1>Header Area - Dashboard</h1>
    <p>This is the dashboard header with metrics</p>
  </div>
);

const LeftSidebarContent1 = () => (
  <SidebarContent>
    <h3>Navigation Menu</h3>
    <NavigationList>
      <li>Dashboard</li>
      <li>Patients</li>
      <li>Exams</li>
      <li>Reports</li>
      <li>Settings</li>
    </NavigationList>
  </SidebarContent>
);

const LeftSidebarContent2 = () => (
  <div>
    <h3>Quick Actions</h3>
    <ul>
      <li>New Patient</li>
      <li>Upload Exam</li>
      <li>Generate Report</li>
      <li>Bulk Actions</li>
      <li>Help</li>
    </ul>
  </div>
);

const LeftSidebarContent3 = () => (
  <div>
    <h3>Filters & Search</h3>
    <div>
      <p>Search Options:</p>
      <input type="text" placeholder="Search patients..." style={{ width: '100%', marginBottom: '0.5rem' }} />
      <input type="text" placeholder="Filter by date..." style={{ width: '100%', marginBottom: '0.5rem' }} />
      <Button variant={ButtonVariants.PRIMARY}>Apply Filters</Button>
    </div>
  </div>
);

const RightSidebarContent1 = () => (
  <div>
    <h3>Quick Actions</h3>
    <div>
      <h4>Common Tasks</h4>
      <Button variant={ButtonVariants.PRIMARY} style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
        Save
      </Button>
      <Button variant={ButtonVariants.SECONDARY} style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
        Export
      </Button>
      <Button variant={ButtonVariants.SECONDARY} style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
        Print
      </Button>
    </div>
  </div>
);

const RightSidebarContent2 = () => (
  <div>
    <h3>Recent Activity</h3>
    <div>
      <p style={{ fontSize: '0.9rem', color: '#666' }}>Latest Updates:</p>
      <ul style={{ fontSize: '0.8rem' }}>
        <li>Patient #123 uploaded</li>
        <li>Report #456 generated</li>
        <li>Exam #789 processed</li>
      </ul>
    </div>
  </div>
);

const TabsContent = () => (
  <div>
    <TabButton $active>Overview</TabButton>
    <TabButton>Details</TabButton>
    <TabButton>Settings</TabButton>
    <TabButton>History</TabButton>
  </div>
);

const BreadcrumbContent = () => (
  <div>
    <span>Home</span> / <span>Components</span> / <span>AutoPanelLayout</span> / <span>Test</span>
  </div>
);

const ActionButtonsContent = () => (
  <div>
    <Button variant={ButtonVariants.PRIMARY}>Save Changes</Button>
    <Button variant={ButtonVariants.SECONDARY}>Cancel</Button>
    <Button variant={ButtonVariants.SECONDARY}>Export</Button>
  </div>
);

const FooterContent = () => (
  <div>
    <p>&copy; 2024 Compumed. All rights reserved. | Status: Connected | Version: 1.0.0</p>
  </div>
);

// Iframe content component for testing external content integration
const IframeContent: React.FC<{ url: string; title: string }> = ({ url, title }) => <EmbeddedViewer url={url} title={title} showHeader={true} />;

const MainContent = () => (
  <div>
    <h2>Main Content Area</h2>
    <p>This is the primary content area where the main application content is displayed.</p>

    <div style={{ marginTop: '1.5rem' }}>
      <h3>AutoPanelLayout Testing</h3>
      <p>
        This test page demonstrates the AutoPanelLayout system with support for multiple content testing. Try adding multiple different contents to the same
        panel area to see the ContentConflictResolver in action.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', margin: '1rem 0' }}>
        <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h4>Conflict Resolution</h4>
          <p>The system automatically detects when multiple components try to render in the same panel area.</p>
        </div>
        <div style={{ padding: '1rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h4>Tab Strategy</h4>
          <p>Multiple different contents are resolved using a tabbed interface for easy switching.</p>
        </div>
      </div>

      <p>Check the console for detailed ContentConflictResolver logs showing the resolution process.</p>
    </div>
  </div>
);

// Control Section Components for Tabbed Interface
const BasicPanelControlsSection: React.FC<{
  activePanels: Set<LayoutArea>;
  togglePanel: (area: LayoutArea) => void;
  showResizeBars: boolean;
  toggleResizeBars: () => void;
  fullscreen: boolean;
  toggleFullscreen: () => void;
}> = ({ activePanels, togglePanel, showResizeBars, toggleResizeBars, fullscreen, toggleFullscreen }) => (
  <ControlSectionContent>
    <h3>Basic Panel Controls</h3>
    <PanelToggles>
      <PanelToggle>
        <input type="checkbox" id="header" checked={activePanels.has(LayoutArea.HEADER)} onChange={() => togglePanel(LayoutArea.HEADER)} />
        <label htmlFor="header">Header</label>
      </PanelToggle>
      <PanelToggle>
        <input type="checkbox" id="content" checked={activePanels.has(LayoutArea.CONTENT)} onChange={() => togglePanel(LayoutArea.CONTENT)} />
        <label htmlFor="content">Content</label>
      </PanelToggle>
      <PanelToggle>
        <input type="checkbox" id="left-sidebar" checked={activePanels.has(LayoutArea.LEFT_SIDEBAR)} onChange={() => togglePanel(LayoutArea.LEFT_SIDEBAR)} />
        <label htmlFor="left-sidebar">Left Sidebar</label>
      </PanelToggle>
      <PanelToggle>
        <input type="checkbox" id="right-sidebar" checked={activePanels.has(LayoutArea.RIGHT_SIDEBAR)} onChange={() => togglePanel(LayoutArea.RIGHT_SIDEBAR)} />
        <label htmlFor="right-sidebar">Right Sidebar</label>
      </PanelToggle>

      <PanelToggle>
        <input type="checkbox" id="footer" checked={activePanels.has(LayoutArea.FOOTER)} onChange={() => togglePanel(LayoutArea.FOOTER)} />
        <label htmlFor="footer">Footer</label>
      </PanelToggle>
      <PanelToggle>
        <input type="checkbox" id="resize-bars" checked={showResizeBars} onChange={toggleResizeBars} />
        <label htmlFor="resize-bars">Show Resize Bars</label>
      </PanelToggle>
      <PanelToggle>
        <input type="checkbox" id="fullscreen" checked={fullscreen} onChange={toggleFullscreen} />
        <label htmlFor="fullscreen">Fullscreen Layout</label>
      </PanelToggle>
    </PanelToggles>
  </ControlSectionContent>
);

const MultipleContentTestingSection: React.FC<{
  activePanels: Set<LayoutArea>;
  multipleContents: Record<string, boolean>;
  toggleMultipleContent: (key: string) => void;
}> = ({ activePanels, multipleContents, toggleMultipleContent }) => (
  <ControlSectionContent>
    <h3>Multiple Content Testing (Conflict Resolution)</h3>
    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
      Enable multiple different contents for the same panel area to test conflict resolution strategies like tabs, modals, etc.
    </p>
    <PanelToggles>
      <PanelToggle>
        <input
          type="checkbox"
          id="header2"
          checked={multipleContents.header2}
          onChange={() => toggleMultipleContent('header2')}
          disabled={!activePanels.has(LayoutArea.HEADER)}
        />
        <label htmlFor="header2">Header Variant 2</label>
      </PanelToggle>
      <PanelToggle>
        <input
          type="checkbox"
          id="leftSidebar2"
          checked={multipleContents.leftSidebar2}
          onChange={() => toggleMultipleContent('leftSidebar2')}
          disabled={!activePanels.has(LayoutArea.LEFT_SIDEBAR)}
        />
        <label htmlFor="leftSidebar2">Left Sidebar Content 2</label>
      </PanelToggle>
      <PanelToggle>
        <input
          type="checkbox"
          id="leftSidebar3"
          checked={multipleContents.leftSidebar3}
          onChange={() => toggleMultipleContent('leftSidebar3')}
          disabled={!activePanels.has(LayoutArea.LEFT_SIDEBAR)}
        />
        <label htmlFor="leftSidebar3">Left Sidebar Content 3</label>
      </PanelToggle>
      <PanelToggle>
        <input
          type="checkbox"
          id="rightSidebar2"
          checked={multipleContents.rightSidebar2}
          onChange={() => toggleMultipleContent('rightSidebar2')}
          disabled={!activePanels.has(LayoutArea.RIGHT_SIDEBAR)}
        />
        <label htmlFor="rightSidebar2">Right Sidebar Content 2</label>
      </PanelToggle>
    </PanelToggles>
  </ControlSectionContent>
);

const DebugControlsSection = () => (
  <ControlSectionContent>
    <h3>Debug Controls</h3>
    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      <Button
        variant={ButtonVariants.SECONDARY}
        onClick={() => console.log('Debug Info:', (window as { DebugFeature?: unknown }).DebugFeature || 'DebugFeature not available')}
      >
        Console Debug Info
      </Button>
      <Button variant={ButtonVariants.SECONDARY} onClick={() => (window as { DebugFeature?: { enable?: () => void } }).DebugFeature?.enable?.()}>
        Enable Debug Panel
      </Button>
      <Button
        variant={ButtonVariants.SECONDARY}
        onClick={() => (window as { DebugFeature?: { setTab?: (tab: string) => void } }).DebugFeature?.setTab?.('autopanel')}
      >
        Show AutoPanel Debug
      </Button>
      <Button variant={ButtonVariants.SECONDARY} onClick={() => console.clear()}>
        Clear Console
      </Button>
    </div>
    <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.5rem' }}>
      Try: <kbd>Ctrl+Shift+D</kbd> for debug panel, or use <code>DebugFeature.help()</code> in console
    </p>
  </ControlSectionContent>
);

interface IframeConfig {
  url: string;
  title: string;
  area: LayoutArea;
  priority: number;
  behavior: 'replace' | 'tabs' | 'modal';
}

const ExternalContentSection: React.FC<{
  iframeConfig: IframeConfig;
  setIframeConfig: (config: IframeConfig) => void;
  loadIframeContent: () => void;
  clearAllIframes: () => void;
  loadedIframes: Set<string>;
}> = ({ iframeConfig, setIframeConfig, loadIframeContent, clearAllIframes, loadedIframes }) => (
  <ControlSectionContent>
    <h3>External Content Resolver Testing</h3>
    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
      Test loading external content via iframe into different panel areas with various resolution behaviors.
    </p>

    <IframeConfigGrid>
      <div>
        <label htmlFor="iframe-url">URL:</label>
        <input
          id="iframe-url"
          type="text"
          value={iframeConfig.url}
          onChange={(e) => setIframeConfig({ ...iframeConfig, url: e.target.value })}
          placeholder="https://example.com"
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <label htmlFor="iframe-title">Title:</label>
        <input
          id="iframe-title"
          type="text"
          value={iframeConfig.title}
          onChange={(e) => setIframeConfig({ ...iframeConfig, title: e.target.value })}
          placeholder="External Content"
          style={{ width: '100%' }}
        />
      </div>
      <div>
        <label htmlFor="iframe-area">Target Panel:</label>
        <select
          id="iframe-area"
          value={iframeConfig.area}
          onChange={(e) => setIframeConfig({ ...iframeConfig, area: e.target.value as LayoutArea })}
          style={{ width: '100%' }}
        >
          <option value={LayoutArea.LEFT_SIDEBAR}>Left Sidebar</option>
          <option value={LayoutArea.RIGHT_SIDEBAR}>Right Sidebar</option>
          <option value={LayoutArea.HEADER}>Header</option>
          <option value={LayoutArea.CONTENT}>Content</option>
          <option value={LayoutArea.FOOTER}>Footer</option>
        </select>
      </div>
      <div>
        <label htmlFor="iframe-behavior">Behavior:</label>
        <select
          id="iframe-behavior"
          value={iframeConfig.behavior}
          onChange={(e) => setIframeConfig({ ...iframeConfig, behavior: e.target.value as 'replace' | 'tabs' | 'modal' })}
          style={{ width: '100%' }}
        >
          <option value="replace">Replace Content</option>
          <option value="tabs">Create Tabs</option>
          <option value="modal">Modal Overlay</option>
        </select>
      </div>
      <div>
        <label htmlFor="iframe-priority">Priority:</label>
        <input
          id="iframe-priority"
          type="number"
          value={iframeConfig.priority}
          onChange={(e) => setIframeConfig({ ...iframeConfig, priority: parseInt(e.target.value) || 0 })}
          min="0"
          max="100"
          style={{ width: '100%' }}
        />
      </div>
    </IframeConfigGrid>

    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
      <Button variant={ButtonVariants.PRIMARY} onClick={loadIframeContent}>
        Load Iframe Content
      </Button>
      <Button variant={ButtonVariants.SECONDARY} onClick={clearAllIframes}>
        Clear All Iframes
      </Button>
      <Button
        variant={ButtonVariants.SECONDARY}
        onClick={() =>
          setIframeConfig({
            ...iframeConfig,
            url: 'https://docs.github.com/en/get-started/start-your-journey/hello-world',
          })
        }
      >
        Load GitHub Docs
      </Button>
      <Button
        variant={ButtonVariants.SECONDARY}
        onClick={() =>
          setIframeConfig({
            ...iframeConfig,
            url: 'https://react.dev/learn',
          })
        }
      >
        Load React Docs
      </Button>
    </div>

    {loadedIframes.size > 0 && (
      <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
        <strong>Loaded iframes:</strong> {loadedIframes.size} active
      </div>
    )}
  </ControlSectionContent>
);

const ConfigurationDisplaySection: React.FC<{
  activePanels: Set<LayoutArea>;
  multipleContents: Record<string, boolean>;
}> = ({ activePanels, multipleContents }) => (
  <ControlSectionContent>
    <h3>Current Configuration</h3>
    <ConfigDisplay>
      <p>
        <strong>Active Panels:</strong> {Array.from(activePanels).join(', ')}
      </p>
      <p>
        <strong>Multiple Contents:</strong>{' '}
        {Object.entries(multipleContents)
          .filter(([, enabled]) => enabled)
          .map(([key]) => key)
          .join(', ') || 'None'}
      </p>
    </ConfigDisplay>
  </ControlSectionContent>
);

// New section for demonstrating the 5 common layout patterns
const LayoutPatternsSection: React.FC<{
  onLoadPattern: (pattern: LayoutPattern) => void;
  currentPattern?: LayoutPattern;
}> = ({ onLoadPattern, currentPattern }) => (
  <ControlSectionContent>
    <h3>Auto-Layout Patterns</h3>
    <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
      Click to automatically configure panels for common layout scenarios. The system will auto-detect the optimal nested structure.
    </p>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
      <PatternCard
        pattern={LayoutPattern.VERTICAL_STACK}
        title="Simple Vertical"
        description="Header → Content → Footer"
        active={currentPattern === LayoutPattern.VERTICAL_STACK}
        onClick={() => onLoadPattern(LayoutPattern.VERTICAL_STACK)}
      />

      <PatternCard
        pattern={LayoutPattern.HORIZONTAL_STACK}
        title="Simple Horizontal"
        description="Sidebar ← → Content ← → Sidebar"
        active={currentPattern === LayoutPattern.HORIZONTAL_STACK}
        onClick={() => onLoadPattern(LayoutPattern.HORIZONTAL_STACK)}
      />

      <PatternCard
        pattern={LayoutPattern.HEADER_HORIZONTAL_FOOTER}
        title="Header + Horizontal + Footer"
        description="Header → (Sidebar + Content) → Footer"
        active={currentPattern === LayoutPattern.HEADER_HORIZONTAL_FOOTER}
        onClick={() => onLoadPattern(LayoutPattern.HEADER_HORIZONTAL_FOOTER)}
      />

      <PatternCard
        pattern={LayoutPattern.SIDEBAR_VERTICAL_MAIN}
        title="Sidebar + Vertical Main"
        description="(Sidebar) + (Header → Content → Footer)"
        active={currentPattern === LayoutPattern.SIDEBAR_VERTICAL_MAIN}
        onClick={() => onLoadPattern(LayoutPattern.SIDEBAR_VERTICAL_MAIN)}
      />

      <PatternCard
        pattern={LayoutPattern.MULTI_SIDEBAR_MAIN}
        title="Multi-Sidebar + Main"
        description="(Nav + Sidebar) + (Header → Content → Footer)"
        active={currentPattern === LayoutPattern.MULTI_SIDEBAR_MAIN}
        onClick={() => onLoadPattern(LayoutPattern.MULTI_SIDEBAR_MAIN)}
      />
    </div>

    <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '1rem' }}>
      <strong>Auto-Detection:</strong> The system automatically detects the optimal layout pattern based on which panels are visible. These buttons show how
      different panel combinations result in different nested structures.
    </div>
  </ControlSectionContent>
);

// Pattern card component
const PatternCard: React.FC<{
  pattern: LayoutPattern;
  title: string;
  description: string;
  active: boolean;
  onClick: () => void;
}> = ({ pattern, title, description, active, onClick }) => (
  <div
    style={{
      border: `2px solid ${active ? '#007bff' : '#ddd'}`,
      borderRadius: '8px',
      padding: '1rem',
      cursor: 'pointer',
      backgroundColor: active ? '#f0f8ff' : 'white',
      transition: 'all 0.2s ease',
    }}
    onClick={onClick}
  >
    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: active ? '#007bff' : '#333' }}>{title}</h4>
    <p style={{ margin: '0', fontSize: '0.8rem', color: '#666' }}>{description}</p>
    {active && <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#007bff', fontWeight: 'bold' }}>✓ ACTIVE</div>}
  </div>
);

// Component that sets up the tabbed control interface using AutoPanelLayout's built-in tab system
const TabbedControlsSetup: React.FC<{
  activePanels: Set<LayoutArea>;
  multipleContents: Record<string, boolean>;
  togglePanel: (area: LayoutArea) => void;
  toggleMultipleContent: (key: string) => void;
  iframeConfig: IframeConfig;
  setIframeConfig: (config: IframeConfig) => void;
  loadIframeContent: () => void;
  clearAllIframes: () => void;
  loadedIframes: Set<string>;
  showResizeBars: boolean;
  toggleResizeBars: () => void;
  fullscreen: boolean;
  toggleFullscreen: () => void;
  loadLayoutPattern: (pattern: LayoutPattern) => void;
  currentPattern?: LayoutPattern;
}> = ({
  activePanels,
  multipleContents,
  togglePanel,
  toggleMultipleContent,
  iframeConfig,
  setIframeConfig,
  loadIframeContent,
  clearAllIframes,
  loadedIframes,
  showResizeBars,
  toggleResizeBars,
  fullscreen,
  toggleFullscreen,
  loadLayoutPattern,
  currentPattern,
}) => {
  const { setPanel } = useAutoLayout();

  // Set up the tabbed content using the AutoPanelLayout's conflict resolution system
  React.useEffect(() => {
    console.log('[TabbedControlsSetup] Setting up tab content entries');

    // Add all control sections as separate content entries to the CONTENT area
    // This will automatically trigger the tab system when multiple contents conflict
    setPanel(
      LayoutArea.CONTENT,
      <BasicPanelControlsSection
        activePanels={activePanels}
        togglePanel={togglePanel}
        showResizeBars={showResizeBars}
        toggleResizeBars={toggleResizeBars}
        fullscreen={fullscreen}
        toggleFullscreen={toggleFullscreen}
      />,
      {
        contentId: 'basic-controls',
        source: 'Basic Controls',
        priority: 5,
      },
    );

    setPanel(
      LayoutArea.CONTENT,
      <MultipleContentTestingSection activePanels={activePanels} multipleContents={multipleContents} toggleMultipleContent={toggleMultipleContent} />,
      {
        contentId: 'multiple-content',
        source: 'Multiple Content',
        priority: 4,
      },
    );

    setPanel(LayoutArea.CONTENT, <DebugControlsSection />, {
      contentId: 'debug-controls',
      source: 'Debug Tools',
      priority: 3,
    });

    setPanel(
      LayoutArea.CONTENT,
      <ExternalContentSection
        iframeConfig={iframeConfig}
        setIframeConfig={setIframeConfig}
        loadIframeContent={loadIframeContent}
        clearAllIframes={clearAllIframes}
        loadedIframes={loadedIframes}
      />,
      {
        contentId: 'external-content',
        source: 'External Content',
        priority: 2,
      },
    );

    setPanel(LayoutArea.CONTENT, <ConfigurationDisplaySection activePanels={activePanels} multipleContents={multipleContents} />, {
      contentId: 'configuration',
      source: 'Configuration',
      priority: 1,
    });

    setPanel(LayoutArea.CONTENT, <LayoutPatternsSection onLoadPattern={loadLayoutPattern} currentPattern={currentPattern} />, {
      contentId: 'layout-patterns',
      source: 'Layout Patterns',
      priority: 6, // High priority for easy access
    });

    console.log('[TabbedControlsSetup] All 6 tab content entries added (including Layout Patterns)');
  }, [
    setPanel,
    activePanels,
    multipleContents,
    togglePanel,
    toggleMultipleContent,
    iframeConfig,
    setIframeConfig,
    loadIframeContent,
    clearAllIframes,
    loadedIframes,
    showResizeBars,
    toggleResizeBars,
    fullscreen,
    toggleFullscreen,
    loadLayoutPattern,
    currentPattern,
  ]);

  return null; // This component only sets up the panels, doesn't render anything itself
};

/**
 * Inner component that has access to the AutoLayout context
 */
const AutoPanelTestLayoutInner: React.FC<{
  activePanels: Set<LayoutArea>;
  multipleContents: Record<string, boolean>;
  iframeConfig: IframeConfig;
  loadedIframes: Set<string>;
  onPanelChange: (area: LayoutArea, isVisible: boolean, size?: number) => void;
}> = ({ activePanels, multipleContents, iframeConfig, loadedIframes, onPanelChange: _onPanelChange }) => {
  const { setPanel, clearPanel } = useAutoLayout();

  // Set up sidebar panels using the context
  useEffect(() => {
    if (activePanels.has(LayoutArea.LEFT_SIDEBAR)) {
      setPanel(LayoutArea.LEFT_SIDEBAR, <LeftSidebarContent1 />, { source: 'test-content-1' });

      // Add additional content if enabled
      if (multipleContents.leftSidebar2) {
        setPanel(LayoutArea.LEFT_SIDEBAR, <LeftSidebarContent2 />, {
          source: 'test-content-2',
          priority: 1,
        });
      }
      if (multipleContents.leftSidebar3) {
        setPanel(LayoutArea.LEFT_SIDEBAR, <LeftSidebarContent3 />, {
          source: 'test-content-3',
          priority: 2,
        });
      }
    } else {
      clearPanel(LayoutArea.LEFT_SIDEBAR);
    }
  }, [activePanels, multipleContents, setPanel, clearPanel]);

  useEffect(() => {
    if (activePanels.has(LayoutArea.RIGHT_SIDEBAR)) {
      setPanel(LayoutArea.RIGHT_SIDEBAR, <RightSidebarContent1 />, { source: 'test-content-1' });

      // Add additional content if enabled
      if (multipleContents.rightSidebar2) {
        setPanel(LayoutArea.RIGHT_SIDEBAR, <RightSidebarContent2 />, {
          source: 'test-content-2',
          priority: 1,
        });
      }
    } else {
      clearPanel(LayoutArea.RIGHT_SIDEBAR);
    }
  }, [activePanels, multipleContents, setPanel, clearPanel]);

  // Handle iframe content loading
  useEffect(() => {
    if (loadedIframes.size > 0) {
      const latestIframe = Array.from(loadedIframes).slice(-1)[0];
      const { url, title: _title, area, priority: _priority, behavior } = iframeConfig;

      const iframeContent = <IframeContent url={url} title={iframeConfig.title} />;

      // Load iframe content with specific behavior
      if (behavior === 'replace') {
        setPanel(area, iframeContent, {
          source: `iframe-${latestIframe}`,
          priority: iframeConfig.priority,
          forceReplace: true,
        });
      } else {
        setPanel(area, iframeContent, {
          source: `iframe-${latestIframe}`,
          priority: iframeConfig.priority,
        });
      }
    }
  }, [loadedIframes, iframeConfig, setPanel]);

  return <MainContent />;
};

/**
 * Enhanced test page component to demonstrate AutoPanelLayout functionality
 * with multiple content testing and conflict resolution
 */
export const AutoPanelLayoutTestPage: React.FC = () => {
  const [activePanels, setActivePanels] = useState<Set<LayoutArea>>(new Set([LayoutArea.CONTENT]));
  const [multipleContents, setMultipleContents] = useState<Record<string, boolean>>({
    header2: false,
    leftSidebar2: false,
    leftSidebar3: false,
    rightSidebar2: false,
  });

  // Layout options
  const [showResizeBars, setShowResizeBars] = useState<boolean>(false);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const [currentPattern, setCurrentPattern] = useState<LayoutPattern>();

  // Iframe test panel state
  const [iframeConfig, setIframeConfig] = useState<IframeConfig>({
    url: 'https://example.com',
    title: 'External Content',
    area: LayoutArea.RIGHT_SIDEBAR,
    priority: 10,
    behavior: 'replace',
  });

  const [loadedIframes, setLoadedIframes] = useState<Set<string>>(new Set());

  const togglePanel = useCallback((area: LayoutArea) => {
    setActivePanels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(area)) {
        newSet.delete(area);
      } else {
        newSet.add(area);
      }
      return newSet;
    });
  }, []);

  const toggleMultipleContent = useCallback((key: string) => {
    setMultipleContents((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const toggleResizeBars = useCallback(() => {
    setShowResizeBars((prev) => !prev);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setFullscreen((prev) => !prev);
  }, []);

  const handlePanelChange = useCallback((area: LayoutArea, isVisible: boolean, size?: number) => {
    console.log(`Panel ${area} changed: visible=${isVisible}, size=${size}`);
  }, []);

  // Create resize handlers to demonstrate pixel-aware functionality
  const resizeHandlers = {
    onDragStart: (area: LayoutArea, initialSize: number) => {
      console.log(`🎯 Drag started on ${area}: ${initialSize}%`);
    },
    onDragEnd: (area: LayoutArea, finalSize: number) => {
      console.log(`🎯 Drag ended on ${area}: ${finalSize}%`);
    },
    onResize: (area: LayoutArea, currentSize: number, constraints: any) => {
      console.log(`🎯 Resizing ${area}: ${currentSize}% (${Math.round((currentSize / 100) * window.innerWidth)}px)`, {
        constraints: {
          minPixels: constraints.minPixels,
          maxPixels: constraints.maxPixels,
          canCollapse: constraints.canCollapse,
        },
      });
    },
    onCollapse: (area: LayoutArea, isCollapsed: boolean) => {
      console.log(`🎯 Panel ${area} ${isCollapsed ? 'collapsed' : 'expanded'}`);
    },
    onAutoResize: (area: LayoutArea, reason: 'viewport' | 'content' | 'constraint', newSize: number) => {
      console.log(`🎯 Auto-resized ${area} due to ${reason}: ${newSize}%`);
    },
  };

  const loadIframeContent = useCallback(() => {
    const { url, title: _title, area, priority: _priority, behavior: _behavior } = iframeConfig;
    const iframeId = `iframe-${Date.now()}`;

    setLoadedIframes((prev) => new Set([...prev, iframeId]));

    // Enable the target panel area
    setActivePanels((prev) => new Set([...prev, area]));

    console.log(`Loading iframe content: ${url} into ${area} with ${iframeConfig.behavior} behavior`);
  }, [iframeConfig]);

  const clearAllIframes = useCallback(() => {
    setLoadedIframes(new Set());
    console.log('Cleared all iframe contents');
  }, []);

  // Pattern loading functionality
  const loadLayoutPattern = useCallback((pattern: LayoutPattern) => {
    console.log(`🎯 Loading Layout Pattern: ${pattern}`);
    setCurrentPattern(pattern);

    // Configure panels based on pattern
    switch (pattern) {
      case LayoutPattern.VERTICAL_STACK:
        setActivePanels(new Set([LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER]));
        break;

      case LayoutPattern.HORIZONTAL_STACK:
        setActivePanels(new Set([LayoutArea.LEFT_SIDEBAR, LayoutArea.CONTENT, LayoutArea.RIGHT_SIDEBAR]));
        break;

      case LayoutPattern.HEADER_HORIZONTAL_FOOTER:
        setActivePanels(new Set([LayoutArea.HEADER, LayoutArea.LEFT_SIDEBAR, LayoutArea.CONTENT, LayoutArea.RIGHT_SIDEBAR, LayoutArea.FOOTER]));
        break;

      case LayoutPattern.SIDEBAR_VERTICAL_MAIN:
        setActivePanels(new Set([LayoutArea.LEFT_SIDEBAR, LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER]));
        break;

      case LayoutPattern.MULTI_SIDEBAR_MAIN:
        setActivePanels(new Set([LayoutArea.LEFT_SIDEBAR, LayoutArea.RIGHT_SIDEBAR, LayoutArea.HEADER, LayoutArea.CONTENT, LayoutArea.FOOTER]));
        break;

      default:
        setActivePanels(new Set([LayoutArea.CONTENT]));
    }

    // Reset multiple contents for cleaner demonstration
    setMultipleContents({
      header2: false,
      leftSidebar2: false,
      leftSidebar3: false,
      rightSidebar2: false,
    });

    // Enable resize bars to show layout structure
    setShowResizeBars(true);
  }, []);

  // Create panel config for resizable panels with pixel constraints
  const panelConfig = showResizeBars
    ? {
        [LayoutArea.LEFT_SIDEBAR]: {
          resizable: true,
          minPixels: 150, // Minimum width for usable sidebar
          maxPixels: 500, // Maximum to prevent dominating screen
          autoSize: 'content' as const,
          collapseThreshold: 120,
        },
        [LayoutArea.RIGHT_SIDEBAR]: {
          resizable: true,
          minPixels: 150,
          maxPixels: 400,
          autoSize: 'content' as const,
          collapseThreshold: 120,
        },
        [LayoutArea.HEADER]: {
          minPixels: 40,
          maxPixels: 100,
          autoSize: 'fixed' as const,
        },
        [LayoutArea.FOOTER]: {
          minPixels: 30,
          maxPixels: 80,
          autoSize: 'fixed' as const,
        },
      }
    : {};

  // Layout props with intelligent auto-layout and pixel-aware sizing
  const layoutProps = {
    autoSaveId: 'test-layout-tabbed',
    onPanelChange: handlePanelChange,
    className: 'auto-panel-test-layout',
    panelConfig,
    enablePixelSizing: true, // Enable the new pixel-based sizing
    viewportPadding: 20, // Reserve 20px for scrollbars/margins
    resizeHandlers, // Add the resize handlers for pixel-aware functionality

    // Auto-layout strategy configuration
    responsive: {
      mobile: {
        sidebarMode: 'overlay' as const,
        compactSpacing: true,
        collapsePanels: [LayoutArea.LEFT_SIDEBAR, LayoutArea.RIGHT_SIDEBAR] as LayoutArea[],
      },
      tablet: {
        sidebarMode: 'stack' as const,
        compactSpacing: false,
        collapsePanels: [LayoutArea.RIGHT_SIDEBAR] as LayoutArea[],
      },
      desktop: {
        sidebarMode: 'stack' as const,
        compactSpacing: false,
        collapsePanels: [] as LayoutArea[],
      },
    },

    // Content for core areas
    header: activePanels.has(LayoutArea.HEADER) ? multipleContents.header2 ? <HeaderContent2 /> : <HeaderContent1 /> : undefined,
    footer: activePanels.has(LayoutArea.FOOTER) ? <FooterContent /> : undefined,
  };

  return (
    <AutoPanelLayout
      {...layoutProps}
      onPanelChange={handlePanelChange}
      header={
        <div>
          <h2>AutoPanelLayout Enhanced Test Page</h2>
          <p>Use the control tabs below to configure the layout and test different panel combinations</p>
        </div>
      }
    >
      <TestPageContent>
        <TabbedControlsSetup
          activePanels={activePanels}
          multipleContents={multipleContents}
          togglePanel={togglePanel}
          toggleMultipleContent={toggleMultipleContent}
          iframeConfig={iframeConfig}
          setIframeConfig={setIframeConfig}
          loadIframeContent={loadIframeContent}
          clearAllIframes={clearAllIframes}
          loadedIframes={loadedIframes}
          showResizeBars={showResizeBars}
          toggleResizeBars={toggleResizeBars}
          fullscreen={fullscreen}
          toggleFullscreen={toggleFullscreen}
          loadLayoutPattern={loadLayoutPattern}
          currentPattern={currentPattern}
        />
        <AutoPanelTestLayoutInner
          activePanels={activePanels}
          multipleContents={multipleContents}
          iframeConfig={iframeConfig}
          loadedIframes={loadedIframes}
          onPanelChange={handlePanelChange}
        />
      </TestPageContent>
    </AutoPanelLayout>
  );
};

// Main content wrapper to organize the test page content
const TestPageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing30};
  padding: ${({ theme }) => theme.space.spacing20};
  /* No explicit height - let content flow naturally */
`;

const ControlSectionContent = styled.div``;

const PanelToggles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.space.spacing10};
`;

const PanelToggle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing10};

  input[type='checkbox'] {
    margin: 0;
    cursor: pointer;
  }

  label {
    font-size: ${({ theme }) => theme.fontSizes.footnote};
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textPrimary};

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  input:disabled + label {
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: not-allowed;
  }
`;

const ConfigDisplay = styled.div`
  padding: ${({ theme }) => theme.space.spacing10};
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  border-radius: ${({ theme }) => theme.radii.base};
  border: 1px solid ${({ theme }) => theme.colors.borderBase};

  p {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    font-size: ${({ theme }) => theme.fontSizes.footnote};

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;

// Removed redundant styled containers - now using automatic LayoutAreaWrappers

const TabButton = styled.button<{ $active?: boolean }>`
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing10};
  border: 1px solid ${({ theme }) => theme.colors.borderBase};
  border-radius: ${({ theme }) => theme.radii.base};
  background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.palette.white)};
  color: ${({ theme, $active }) => ($active ? theme.colors.palette.white : theme.colors.textPrimary)};
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.palette.grayscale[1])};
  }
`;

// All layout area containers removed - now using automatic LayoutAreaWrappers

// Iframe-specific styled components
const IframeConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;

  label {
    display: block;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 0.25rem;
    color: #333;
  }

  input,
  select {
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
  }
`;

// Iframe components removed - now using EmbeddedViewer component

// Panel content styled components
const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing20};

  h3 {
    margin: 0 0 ${({ theme }) => theme.space.spacing20} 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: ${({ theme }) => theme.fontSizes.body};
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  }
`;

const NavigationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: ${({ theme }) => theme.space.spacing20};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase ?? theme.colors.palette.grayscale[2]};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;
